import React, { useState, useEffect } from 'react';
import {
  generateNewDestiny,
  applyLifeChoice,
  compileLifeSummary,
  getCurrentStageEvents,
  type OneLifeState,
  type LifeEvent,
  type LifeChoice,
  type LifeSummary,
  LIFE_STAGES,
  WORLD_COUNTRIES,
} from '@gamengayxua/shared';
import { api } from '../../api/client.ts';
import { LifeSummaryShareCard } from './LifeSummaryShareCard.tsx';

interface OneLifeGameProps {
  onBackToHub: () => void;
}

export const OneLifeGame: React.FC<OneLifeGameProps> = ({ onBackToHub }) => {
  const [gameState, setGameState] = useState<OneLifeState | null>(null);
  const [currentEvents, setCurrentEvents] = useState<LifeEvent[]>([]);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(0);
  const [lastOutcome, setLastOutcome] = useState<string | null>(null);
  const [lifeSummary, setLifeSummary] = useState<LifeSummary | null>(null);
  const [isBirthRolling, setIsBirthRolling] = useState<boolean>(false);
  const [isFastMode, setIsFastMode] = useState<boolean>(true);
  const [customCountry, setCustomCountry] = useState<string>('');
  const [drawMode, setDrawMode] = useState<'annual_births' | 'population'>('annual_births');
  const [copiedShare, setCopiedShare] = useState<boolean>(false);

  // Load local auto-save on mount
  useEffect(() => {
    const saved = localStorage.getItem('onelife_current_run');
    if (saved) {
      try {
        const parsed: OneLifeState = JSON.parse(saved);
        if (parsed.isAlive) {
          setGameState(parsed);
          setCurrentEvents(getCurrentStageEvents(parsed));
        } else if (parsed.completedAt) {
          setLifeSummary(compileLifeSummary(parsed));
        }
      } catch {}
    }
  }, []);

  // Auto save whenever gameState updates
  useEffect(() => {
    if (gameState) {
      localStorage.setItem('onelife_current_run', JSON.stringify(gameState));
    }
  }, [gameState]);

  // Handle Birth
  const handleBirth = () => {
    setIsBirthRolling(true);
    setLastOutcome(null);
    setLifeSummary(null);

    setTimeout(() => {
      const destiny = generateNewDestiny({
        forcedCountryCode: customCountry || undefined,
        isFastMode,
        drawMode,
      });
      setGameState(destiny);
      const events = getCurrentStageEvents(destiny);
      setCurrentEvents(events);
      setSelectedEventIndex(0);
      setIsBirthRolling(false);
    }, 700);
  };

  // Handle Choice
  const handleMakeChoice = async (event: LifeEvent, choice: LifeChoice) => {
    if (!gameState) return;

    const result = applyLifeChoice(gameState, event, choice);
    setLastOutcome(choice.outcomeTextVi);

    if (result.isDeath) {
      const summary = compileLifeSummary(result.updatedState);
      setGameState(result.updatedState);
      setLifeSummary(summary);
      localStorage.removeItem('onelife_current_run');

      // Sync summary to server in background
      api.completeOneLife(result.updatedState).catch(() => {});
    } else {
      setGameState(result.updatedState);
      const nextEvents = getCurrentStageEvents(result.updatedState);
      setCurrentEvents(nextEvents);
      setSelectedEventIndex(0);
    }
  };

  const currentStage = gameState ? LIFE_STAGES[gameState.currentStageIndex] : null;
  const activeEvent = currentEvents[selectedEventIndex] || currentEvents[0];

  return (
    <div className="max-w-lg mx-auto p-4 space-y-5" style={{ paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Bar Navigation */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-4 py-2.5 text-base sm:text-lg font-bold bg-slate-800/80 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-2"
        >
          <span className="text-xl">←</span>
          <span>Về GameHub</span>
        </button>
        <div className="flex items-center space-x-2.5">
          <span className="text-lg sm:text-xl text-amber-400 font-black tracking-wide">MỘT KIẾP NGƯỜI</span>
          <span className="text-sm px-3.5 py-1.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20 font-black">
            {isFastMode ? 'Nhanh' : 'Sâu'}
          </span>
        </div>
      </div>

      {/* 1. STATE: NOT STARTED -> BIRTH SCREEN */}
      {!gameState && !lifeSummary && (
        <div className="retro-card p-6 sm:p-7 text-center space-y-6">
          <div className="text-7xl animate-bounce">🌍</div>
          <div className="space-y-3">
            <h2 className="text-3xl sm:text-4xl font-black text-amber-400 tracking-wide">MỘT KIẾP NGƯỜI</h2>
            <p className="text-lg sm:text-xl text-slate-300 italic font-medium">"One Life — Many Destinies"</p>
            <p className="text-base sm:text-lg text-slate-100 leading-relaxed pt-2 font-normal">
              Lấy cảm hứng từ 1in8b.life. Bạn sẽ khởi đầu một cuộc đời ngẫu nhiên tại bất kỳ quốc gia nào trên Trái Đất theo dữ liệu nhân khẩu học chính thức của Liên Hợp Quốc (UN WPP 2024) & World Bank.
            </p>
          </div>

          {/* Model Selection: Annual Births vs Total Population */}
          <div className="bg-slate-900/90 p-4 sm:p-5 rounded-xl border border-slate-800 space-y-3.5 text-left">
            <label className="text-base font-black text-amber-300 block">📊 Mô hình phân bổ xác suất:</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setDrawMode('annual_births')}
                className={`p-4 rounded-xl text-left transition-all border ${
                  drawMode === 'annual_births'
                    ? 'bg-amber-500/20 border-amber-500 text-amber-200 shadow-lg'
                    : 'bg-slate-800/50 border-slate-700/60 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <div className="text-base sm:text-lg font-black flex items-center space-x-2">
                  <span>👶</span>
                  <span>Ca sinh hằng năm</span>
                </div>
                <div className="text-sm sm:text-base text-slate-300 mt-2 leading-snug">
                  Chuẩn UN WPP 2024: Xác suất 1 em bé chào đời hôm nay (Nigeria 6%, Trung Quốc 6.7%).
                </div>
              </button>

              <button
                onClick={() => setDrawMode('population')}
                className={`p-4 rounded-xl text-left transition-all border ${
                  drawMode === 'population'
                    ? 'bg-amber-500/20 border-amber-500 text-amber-200 shadow-lg'
                    : 'bg-slate-800/50 border-slate-700/60 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <div className="text-base sm:text-lg font-black flex items-center space-x-2">
                  <span>👥</span>
                  <span>Quy mô dân số</span>
                </div>
                <div className="text-sm sm:text-base text-slate-300 mt-2 leading-snug">
                  Xác suất chọn ngẫu nhiên 1 người trong 8.1 tỷ người đang sống (Ấn Độ 17.8%, TQ 17.5%).
                </div>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/80 p-4 sm:p-5 rounded-xl border border-slate-800 space-y-3 text-left">
            <label className="text-base font-black text-slate-200 block">Tốc độ trải nghiệm:</label>
            <div className="flex space-x-3">
              <button
                onClick={() => setIsFastMode(true)}
                className={`flex-1 py-3.5 text-base sm:text-lg font-black rounded-xl transition-all ${
                  isFastMode
                    ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                ⚡ Nhanh (5-10p)
              </button>
              <button
                onClick={() => setIsFastMode(false)}
                className={`flex-1 py-3.5 text-base sm:text-lg font-black rounded-xl transition-all ${
                  !isFastMode
                    ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                📖 Sâu sắc (15p)
              </button>
            </div>
          </div>

          <button
            onClick={handleBirth}
            disabled={isBirthRolling}
            className="w-full py-5 text-xl sm:text-2xl font-black retro-button tracking-wider flex items-center justify-center space-x-3 uppercase shadow-xl"
          >
            {isBirthRolling ? (
              <span className="animate-spin">🌀 Đang gieo quẻ số mệnh...</span>
            ) : (
              <>
                <span className="text-2xl">🌱</span>
                <span>BẮT ĐẦU CUỘC ĐỜI (MIỄN PHÍ)</span>
              </>
            )}
          </button>
          <p className="text-sm sm:text-base text-slate-300 font-medium">Mô phỏng dựa trên dữ liệu UN Population Division 2024 & World Bank 2023.</p>
        </div>
      )}

      {/* 2. STATE: PLAYING LIFE STAGE */}
      {gameState && gameState.isAlive && !lifeSummary && (
        <div className="space-y-4">
          {/* Identity & Demographics Header */}
          <div className="retro-card p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3.5">
                <span className="text-5xl">{gameState.country.flagEmoji}</span>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-100 leading-tight">{gameState.characterName}</h3>
                  <p className="text-base sm:text-lg text-slate-200 font-bold mt-1">
                    {gameState.gender === 'male' ? 'Nam' : 'Nữ'} • {gameState.country.nameVi} ({gameState.country.nameEn})
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-3xl sm:text-4xl font-black text-amber-400">{gameState.currentAge} tuổi</span>
                <p className="text-base text-amber-200/90 font-black mt-0.5">{currentStage?.titleVi}</p>
              </div>
            </div>

            <div className="text-base bg-slate-900/90 p-4 rounded-xl border border-slate-800 space-y-2.5">
              <div className="flex justify-between items-center text-slate-200">
                <span className="text-slate-400 font-semibold">📍 Nơi sống:</span>
                <span className="font-bold text-slate-100 text-base">{gameState.regionNameVi}</span>
              </div>
              <div className="flex justify-between items-center text-slate-200">
                <span className="text-slate-400 font-semibold">🏠 Xuất thân:</span>
                <span className="font-bold text-amber-300 text-base">{gameState.familyClassVi}</span>
              </div>
              <div className="flex justify-between items-center text-slate-200">
                <span className="text-slate-400 font-semibold">💼 Nghề nghiệp:</span>
                <span className="font-bold text-cyan-300 text-base">{gameState.career}</span>
              </div>
              <div className="flex justify-between text-slate-300 border-t border-slate-800/80 pt-2.5 text-base">
                <span className="text-slate-400 font-semibold">📊 Dân số & Kinh tế:</span>
                <span className="text-slate-200 font-bold">
                  {gameState.country.annualBirthsMillion}M ca sinh/năm • GDP ${gameState.country.gdpPerCapitaUsd.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Character 5 Core Attributes Bar */}
            <div className="grid grid-cols-5 gap-2.5 pt-1 text-center">
              <div className="bg-slate-900/90 p-2.5 rounded-xl border border-rose-500/40">
                <span className="text-2xl">❤️</span>
                <p className="text-sm font-bold text-slate-200 mt-1">Sức khỏe</p>
                <p className="text-lg sm:text-xl font-black text-rose-400">{gameState.attributes.health}%</p>
              </div>
              <div className="bg-slate-900/90 p-2.5 rounded-xl border border-amber-500/40">
                <span className="text-2xl">😊</span>
                <p className="text-sm font-bold text-slate-200 mt-1">Hạnh phúc</p>
                <p className="text-lg sm:text-xl font-black text-amber-400">{gameState.attributes.happiness}%</p>
              </div>
              <div className="bg-slate-900/90 p-2.5 rounded-xl border border-emerald-500/40">
                <span className="text-2xl">💰</span>
                <p className="text-sm font-bold text-slate-200 mt-1">Tài sản</p>
                <p className="text-lg sm:text-xl font-black text-emerald-400">{gameState.attributes.wealth}%</p>
              </div>
              <div className="bg-slate-900/90 p-2.5 rounded-xl border border-cyan-500/40">
                <span className="text-2xl">🧠</span>
                <p className="text-sm font-bold text-slate-200 mt-1">Tri thức</p>
                <p className="text-lg sm:text-xl font-black text-cyan-400">{gameState.attributes.intellect}%</p>
              </div>
              <div className="bg-slate-900/90 p-2.5 rounded-xl border border-purple-500/40">
                <span className="text-2xl">🤝</span>
                <p className="text-sm font-bold text-slate-200 mt-1">Quan hệ</p>
                <p className="text-lg sm:text-xl font-black text-purple-400">{gameState.attributes.relations}%</p>
              </div>
            </div>
          </div>

          {/* Last Choice Consequence Feedback */}
          {lastOutcome && (
            <div className="p-4 sm:p-5 bg-amber-500/20 border border-amber-500/50 rounded-xl text-base sm:text-lg text-amber-200 flex items-start space-x-3 leading-relaxed">
              <span className="text-2xl">✨</span>
              <span className="font-bold">{lastOutcome}</span>
            </div>
          )}

          {/* Life Event & Choices Card */}
          {activeEvent && (
            <div className="retro-card p-5 sm:p-6 space-y-5 border-slate-700/80">
              <div className="space-y-2.5">
                <div className="flex items-center space-x-3">
                  <span className="text-base px-3.5 py-1.5 rounded-lg bg-blue-500/20 text-blue-300 font-black border border-blue-500/30">
                    Năm {activeEvent.age} tuổi
                  </span>
                  <span className="text-base text-slate-300 font-bold uppercase tracking-wider">Ngã rẽ cuộc đời</span>
                </div>
                <h4 className="text-2xl sm:text-3xl font-black text-slate-100 pt-1 leading-snug">{activeEvent.titleVi}</h4>
                <p className="text-lg sm:text-xl text-slate-100 leading-relaxed font-normal pt-1">{activeEvent.descriptionVi}</p>
              </div>

              {/* Choices */}
              <div className="space-y-4 pt-2">
                {activeEvent.choices.map((choice, idx) => (
                  <button
                    key={choice.id}
                    onClick={() => handleMakeChoice(activeEvent, choice)}
                    className="w-full text-left p-5 rounded-xl bg-slate-900/95 hover:bg-slate-850 border border-slate-700 hover:border-amber-400/80 transition-all space-y-2.5 group shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-lg sm:text-xl font-black text-slate-100 group-hover:text-amber-300 leading-snug">
                        {idx + 1}. {choice.textVi}
                      </span>
                      <span className="text-xl sm:text-2xl text-slate-400 group-hover:text-amber-400 font-black ml-2">→</span>
                    </div>
                    {/* Visual Impact Hint */}
                    <div className="flex items-center space-x-4 text-base font-bold">
                      {choice.healthDelta !== 0 && (
                        <span className={choice.healthDelta > 0 ? 'text-rose-400' : 'text-rose-500'}>
                          Sức khỏe {choice.healthDelta > 0 ? `+${choice.healthDelta}` : choice.healthDelta}
                        </span>
                      )}
                      {choice.wealthDelta !== 0 && (
                        <span className={choice.wealthDelta > 0 ? 'text-emerald-400' : 'text-emerald-500'}>
                          Tài sản {choice.wealthDelta > 0 ? `+${choice.wealthDelta}` : choice.wealthDelta}
                        </span>
                      )}
                      {choice.happinessDelta !== 0 && (
                        <span className={choice.happinessDelta > 0 ? 'text-amber-400' : 'text-amber-500'}>
                          Hạnh phúc {choice.happinessDelta > 0 ? `+${choice.happinessDelta}` : choice.happinessDelta}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. STATE: LIFE SUMMARY SCREEN */}
      {lifeSummary && (
        <div className="space-y-5 text-center">
          <LifeSummaryShareCard summary={lifeSummary} />

          <button
            onClick={handleBirth}
            className="w-full py-5 text-xl font-black retro-button tracking-wider uppercase flex items-center justify-center space-x-3 shadow-xl"
          >
            <span className="text-2xl">🔄</span>
            <span>CHƠI LẠI</span>
          </button>
        </div>
      )}
    </div>
  );
};
